/**
 * Keeping an OLD policy's saved values when the master data has moved on.
 *
 * Opening a policy for edit used to swap the CURRENT master values into the
 * form (discount, variant, IDV range, zone, RTO region ...) and force a
 * recalculation, so re-saving overwrote what the policy was actually sold with.
 * Instead, a snapshot of the saved values is taken when the policy loads
 * (savedSnapshotFromPolicy); every place that used to pull a master value
 * checks `keeps(field)` first and leaves the saved one alone, and
 * computeMasterDrift lists what differs so the operator can opt in to the
 * latest values.
 *
 * Two groups stop being "kept" independently once the operator changes the
 * underlying pick: "vehicle" (the Make & Model) and "rto" (the RTO City).
 *
 * Pure functions only — no React, so they are easy to test.
 */
import { routingFromPolicy, sameCompany } from "./companyRules";
import { resolveIdvRange } from "./idvHelpers";

const GST_MULTIPLIER = 1.18;

/** "TN01" -> "TN-01": how both edit loaders normalise a saved RTO code. */
export const normalizeRtoCode = (code) =>
  code && !code.includes("-") && code.length >= 4
    ? `${code.substring(0, 2)}-${code.substring(2)}`
    : code || "";

/** Which release group a guarded form field belongs to. */
export const FIELD_GROUP = {
  discount: "vehicle",
  vehicleVariant: "vehicle",
  modelNotFound: "vehicle",
  manufacturerSellingPrice: "vehicle",
  idvRangeFrom: "vehicle",
  idvRangeTo: "vehicle",
  zeroDepreciationPercentage: "vehicle",
  tppdRestrict: "vehicle",
  zone: "rto",
  RTORegion: "rto",
  RTOCity: "rto",
};

/** Was a value actually stored? false / 0 count; undefined, null and "" do not. */
export const hasSaved = (v) => !(v === undefined || v === null || v === "");

const asText = (v) =>
  v === undefined || v === null ? "" : String(v).trim().toLowerCase();

/** Loose equality for form text vs stored values: trim, case-insensitive,
 *  and numeric when both sides are numbers ("60" equals 60, "60.0" equals 60). */
export const sameValue = (a, b) => {
  const x = asText(a);
  const y = asText(b);
  if (x === y) return true;
  if (x === "" || y === "") return false;
  const nx = Number(x);
  const ny = Number(y);
  return Number.isFinite(nx) && Number.isFinite(ny) && nx === ny;
};

const finiteOrUndefined = (v) => {
  if (v === undefined || v === null || v === "") return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

const truthy = (v) => v === true || asText(v) === "true";

/** The values a saved policy was sold with. Null when there is no policy. */
export const savedSnapshotFromPolicy = (policy) => {
  if (!policy) return null;
  return {
    makeModel: policy.makeModel == null ? "" : String(policy.makeModel),
    rtoCityLocation: normalizeRtoCode(policy.rtoCityLocation),
    // Flipped to false by releaseSaved once the operator changes the pick.
    keep: { vehicle: true, rto: true },
    vehicleMake: policy.vehicleMake,
    vehicleModel: policy.vehicleModel,
    vehicleCC: policy.vehicleCC,
    vehicleVariant: policy.vehicleVariant,
    discount: policy.discount,
    modelNotFound: policy.modelNotFound,
    manufacturerSellingPrice: policy.manufacturerSellingPrice,
    idvRangeFrom: policy.idvRangeFrom,
    idvRangeTo: policy.idvRangeTo,
    tppdRestrict: policy.tppdRestrict,
    paCover: policy.paCover,
    paCoverAmount: policy.paCoverAmount,
    zone: policy.zone,
    RTORegion: policy.RTORegion,
    RTOCity: policy.RTOCity,
    // The backend stores 0 when Zero Depreciation is off, which is not a price.
    zeroDepreciationPercentage: truthy(policy.zeroDepreciation)
      ? policy.zeroDepreciationPercentage
      : undefined,
    routing: routingFromPolicy(policy).settings,
    // Combined Make & Model: the record it was sold from and the TP vehicle snapshot.
    makeModelCombo: policy.makeModelCombo ? String(policy.makeModelCombo) : "",
    tpVehicle: policy.tpVehicle || null,
  };
};

/** Is the saved vehicle still the one on the form (and not released)? */
export const keepsVehicle = (snapshot, makeModelId) =>
  !!snapshot?.keep?.vehicle && String(makeModelId ?? "") === snapshot.makeModel;

/** Is the saved RTO still the one on the form (and not released)? */
export const keepsRto = (snapshot, rtoCode) =>
  !!snapshot?.keep?.rto && String(rtoCode ?? "") === snapshot.rtoCityLocation;

/**
 * The vehicle to PRICE with. While the saved vehicle is kept the premium must
 * use the engine CC it was sold with, not the master's current one.
 * Both spellings are set because calculatePremium reads `CC` before `cc`.
 */
export const vehicleForPricing = (vehicle, snapshot, makeModelId) =>
  vehicle && keepsVehicle(snapshot, makeModelId) && Number(snapshot.vehicleCC) > 0
    ? { ...vehicle, cc: snapshot.vehicleCC, CC: snapshot.vehicleCC }
    : vehicle;

/**
 * A stand-in Make & Model row for a vehicle that has been deleted from the
 * master, built from what the policy was saved with. It mirrors the saved
 * values, so nothing on the form reads as "changed", and its CC is the saved
 * one — the calculator would otherwise fall back to a 160cc default.
 */
export const missingVehicleFromSnapshot = (snapshot, makeModelId) => {
  const s = snapshot || {};
  const label = `${s.vehicleMake} - ${s.vehicleModel}`;
  return {
    _id: makeModelId,
    make: s.vehicleMake,
    model: s.vehicleModel,
    cc: s.vehicleCC,
    vehicleVariant: s.vehicleVariant || "",
    company: s.routing?.ODCompany || "",
    discount: s.discount,
    modelNotFound: !!s.modelNotFound,
    manufacturerSellingPrice: s.manufacturerSellingPrice,
    idvRangeFrom: s.idvRangeFrom,
    idvRangeTo: s.idvRangeTo,
    tppdRequired: !!s.tppdRestrict,
    label,
    value: label,
    _missingFromMaster: true,
  };
};

/**
 * A stand-in calculation result built from the premium the policy was saved
 * with, so an untouched edit needs no recalculation (the form then offers
 * "Next" instead of "Calculate").
 *
 * finalPremium is motor + CPA with GST, deliberately NOT rounded: every reader
 * backs the CPA out again with motorPremiumExcludingPa, which only returns the
 * stored motor premium exactly if nothing was rounded in between.
 *
 * Returns null when the stored figures do not reconcile (a policy saved before
 * the motor / CPA split existed) — the operator then Calculates, as before.
 */
export const calculationFromSavedPolicy = (policy) => {
  if (!policy) return null;
  const stored = policy.premiumCalculation || {};

  const motor =
    finiteOrUndefined(policy.originalFinalPremium) ??
    finiteOrUndefined(stored.finalPremium);
  if (motor === undefined || motor <= 0) return null;

  const pa = truthy(policy.paCover) ? Number(policy.paCoverAmount) || 0 : 0;
  const finalPremium = motor + pa * GST_MULTIPLIER;

  const savedTotal = finiteOrUndefined(policy.finalPremium);
  if (savedTotal === undefined || Math.abs(finalPremium - savedTotal) > 1) {
    return null;
  }

  const tpPremium = finiteOrUndefined(stored.tpPremium);
  return {
    _savedPolicyPremium: true,
    finalPremium,
    netPremiumAB: finiteOrUndefined(stored.netPremium ?? policy.netPremium),
    tax: finiteOrUndefined(stored.tax),
    // Total B carries PA as "PA to Owner Driver"; readers take it back out.
    pAOwnerDriver: pa,
    totalAodPremium: finiteOrUndefined(stored.odPremium),
    totalBodPremium: tpPremium === undefined ? undefined : tpPremium + pa,
  };
};

const money = (v) => `₹${Number(v).toLocaleString("en-IN")}`;
const yesNo = (v) => (v ? "Yes" : "No");
const parseCc = (v) => parseInt(String(v ?? "").replace(/[^0-9]/g, ""), 10);
const route = (od, tp) =>
  !od ? "—" : !tp || sameCompany(od, tp) ? od : `${od} (OD) · ${tp} (TP)`;

/**
 * Where the current master differs from what this policy was saved with.
 *
 * Returns rows `{ key, group, label, saved, current, note?, applicable }`.
 * `applicable: false` rows are informational — "Use latest master values" does
 * not change them. A field with no saved value (policy older than the field)
 * is not drift: it simply falls back to the master, as it always did.
 *
 * @param {object} p
 * @param {object} p.snapshot      savedSnapshotFromPolicy()
 * @param {string} p.makeModelId   the form's makeModel
 * @param {string} p.rtoCode       the form's rtoCityLocation
 * @param {object} p.master        the Make & Model row now on the form
 * @param {string} p.masterDiscount the discount that row resolves to (user's own first)
 * @param {number} p.masterPaMin   the master's PA Cover floor
 * @param {object} p.rto           { region, city, zone, zoneName } of the selected RTO, when loaded
 */
export const computeMasterDrift = ({
  snapshot: s,
  makeModelId,
  rtoCode,
  master: m,
  masterDiscount = "",
  masterPaMin,
  rto,
  // Multiple company: the record on the form (its TP company is the expected TP
  // insurer) and the TP Make & Model row as the master has it NOW.
  combo = null,
  currentTpRow = null,
}) => {
  const rows = [];
  if (!s) return rows;

  const add = (group, key, label, saved, current, extra = {}) =>
    rows.push({ group, key, label, saved, current, applicable: true, ...extra });

  // Only the row the form actually holds — a static list entry or a master that
  // has not loaded yet has no _id and is skipped.
  if (m && String(m._id) === String(makeModelId) && keepsVehicle(s, makeModelId)) {
    if (m._missingFromMaster || m._disabledInMaster) {
      add(
        "vehicle",
        "record",
        "Make & Model record",
        "Available",
        m._missingFromMaster ? "Deleted from the master" : "Disabled in the master",
        {
          applicable: false,
          note: "This policy keeps its saved vehicle; it cannot be picked again for new policies.",
        }
      );
    }

    if (!m._missingFromMaster) {
      if (
        hasSaved(s.vehicleMake) &&
        hasSaved(s.vehicleModel) &&
        (!sameValue(s.vehicleMake, m.make) || !sameValue(s.vehicleModel, m.model))
      ) {
        add(
          "vehicle",
          "name",
          "Make & Model name",
          `${s.vehicleMake} ${s.vehicleModel}`,
          `${m.make} ${m.model}`
        );
      }

      const masterVariant = m.vehicleVariant || "";
      if (hasSaved(s.vehicleVariant) && masterVariant && !sameValue(s.vehicleVariant, masterVariant)) {
        add("vehicle", "variant", "Variant", s.vehicleVariant, masterVariant);
      }

      const savedCc = parseCc(s.vehicleCC);
      const masterCc = parseCc(m.cc);
      if (savedCc > 0 && masterCc > 0 && savedCc !== masterCc) {
        add("vehicle", "cc", "Engine CC", `${savedCc} CC`, `${masterCc} CC`);
      }

      const masterOd = m.company || "";
      // One row = one company; a Combined Make & Model names its own TP insurer.
      const masterTp = combo ? combo.tpCompany || "" : m.company || "";
      if (
        masterOd &&
        (!sameCompany(masterOd, s.routing?.ODCompany) ||
          (masterTp && !sameCompany(masterTp, s.routing?.TPCompany)))
      ) {
        add(
          "vehicle",
          "insurer",
          "Insurer",
          route(s.routing?.ODCompany, s.routing?.TPCompany),
          route(masterOd, masterTp),
          {
            applicable: false,
            note: "Changes which wallet is charged — re-pick the vehicle to re-route.",
          }
        );
      }

      if (hasSaved(s.discount) && masterDiscount !== "" && !sameValue(s.discount, masterDiscount)) {
        add("vehicle", "discount", "Discount %", `${s.discount}%`, `${masterDiscount}%`);
      }

      if (hasSaved(s.modelNotFound) && !!s.modelNotFound !== !!m.modelNotFound) {
        add(
          "vehicle",
          "modelNotFound",
          "Model not in insurer list",
          yesNo(s.modelNotFound),
          yesNo(m.modelNotFound)
        );
      }

      if (
        hasSaved(s.manufacturerSellingPrice) &&
        !sameValue(s.manufacturerSellingPrice, m.manufacturerSellingPrice)
      ) {
        add(
          "vehicle",
          "sellingPrice",
          "Manufacturer selling price",
          money(s.manufacturerSellingPrice),
          hasSaved(m.manufacturerSellingPrice) ? money(m.manufacturerSellingPrice) : "Not set"
        );
      }

      if (hasSaved(s.idvRangeFrom) && hasSaved(s.idvRangeTo)) {
        const range = resolveIdvRange(m);
        if (!sameValue(s.idvRangeFrom, range.from) || !sameValue(s.idvRangeTo, range.to)) {
          add(
            "vehicle",
            "idvRange",
            "IDV range",
            `${money(s.idvRangeFrom)} – ${money(s.idvRangeTo)}`,
            range.from && range.to ? `${money(range.from)} – ${money(range.to)}` : "Not set"
          );
        }
      }

      if (
        hasSaved(s.zeroDepreciationPercentage) &&
        hasSaved(m.nilDipPrice) &&
        !sameValue(s.zeroDepreciationPercentage, m.nilDipPrice)
      ) {
        add(
          "vehicle",
          "nilDip",
          "Nil Dip price",
          String(s.zeroDepreciationPercentage),
          String(m.nilDipPrice)
        );
      }

      // The TP insurer's vehicle (Multiple company): what it was sold with against the master row now.
      const savedTp = s.tpVehicle;
      if (combo && savedTp && currentTpRow) {
        const savedTpName = `${savedTp.vehicleMake || ""} ${savedTp.vehicleModel || ""}`.trim();
        const masterTpName = `${currentTpRow.make || ""} ${currentTpRow.model || ""}`.trim();
        if (savedTpName && masterTpName && !sameValue(savedTpName, masterTpName)) {
          add("vehicle", "tpName", "TP Make & Model name", savedTpName, masterTpName);
        }
        const savedTpVariant = savedTp.vehicleVariant || "";
        const masterTpVariant = currentTpRow.vehicleVariant || "";
        if (hasSaved(savedTpVariant) && masterTpVariant && !sameValue(savedTpVariant, masterTpVariant)) {
          add("vehicle", "tpVariant", "TP Variant", savedTpVariant, masterTpVariant);
        }
        const savedTpCc = parseCc(savedTp.vehicleCC);
        const masterTpCc = parseCc(currentTpRow.cc);
        if (savedTpCc > 0 && masterTpCc > 0 && savedTpCc !== masterTpCc) {
          add("vehicle", "tpCc", "TP Engine CC", `${savedTpCc} CC`, `${masterTpCc} CC`);
        }
      }

      if (s.tppdRestrict === true && !m.tppdRequired) {
        add("vehicle", "tppd", "TPPD Restrict", "On", "No longer offered for this vehicle");
      }

      const savedPa = Number(s.paCoverAmount);
      if (truthy(s.paCover) && savedPa > 0 && Number.isFinite(masterPaMin) && savedPa < masterPaMin) {
        add(
          "vehicle",
          "paMin",
          "PA Cover amount",
          money(savedPa),
          `Below the new ${money(masterPaMin)} minimum`,
          { note: "The saved amount stays valid until you apply the latest values." }
        );
      }
    }
  }

  if (rto && keepsRto(s, rtoCode)) {
    if (hasSaved(s.zone) && rto.zone && !sameValue(s.zone, rto.zone)) {
      add("rto", "zone", "Zone", `Zone ${s.zone}`, `Zone ${rto.zone}`);
    }
    if (hasSaved(s.RTORegion) && !sameValue(s.RTORegion, rto.region)) {
      add("rto", "region", "RTO Region", s.RTORegion, rto.region || "—");
    }
    if (hasSaved(s.RTOCity) && !sameValue(s.RTOCity, rto.city)) {
      add("rto", "city", "RTO City", s.RTOCity, rto.city || "—");
    }
  }

  return rows;
};
